import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API, getAuthToken } from "@/App";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { getBrandPillStyle } from "@/lib/brandColors";
import { extractErrorMessage } from "@/lib/utils";
import { useApprovalThreshold, computeApprovalTrigger } from "@/lib/approvalTrigger";
import { computeSlabValidation, useApplicableSlab } from "@/lib/slabValidation";
import { SellerContributionCard } from "@/components/SellerContributionCard";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Trash2, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { SearchableSelect } from "@/components/ui/searchable-select";
import MultiSelectChips from "@/components/MultiSelectChips";
import { useRemarkCategories } from "@/hooks/useRemarkCategories";
import { useWarehouseAvailability, computeInventoryViolations } from "@/hooks/useInventoryRemaining";
import { useActiveWarehouses } from "@/hooks/useActiveWarehouses";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function CreateQuotation() {
  const navigate = useNavigate();
  const [sellers, setSellers] = useState([]);
  const [products, setProducts] = useState([]);
  const [discountSlabs, setDiscountSlabs] = useState([]);
  const [transporters, setTransporters] = useState([]);
  const { warehouses: activeWarehouses, options: warehouseOptions, loaded: warehousesLoaded, refresh: refreshWarehouses, isActive: isWarehouseActive } = useActiveWarehouses();
  const [pickupWarehouseId, setPickupWarehouseId] = useState("");
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [selectedTransporter, setSelectedTransporter] = useState(null);
  const [quotationType, setQuotationType] = useState("offline");
  const [nextQuotationId, setNextQuotationId] = useState("");
  const [orderDate, setOrderDate] = useState(new Date().toISOString().split('T')[0]); // YYYY-MM-DD format
  const [saleItems, setSaleItems] = useState([]);
  const [freebieItems, setFreebieItems] = useState([]);
  const [approvalOverride, setApprovalOverride] = useState(false);
  const [approvalRemarks, setApprovalRemarks] = useState("");
  const [remarkCategories, setRemarkCategories] = useState([]);
  const approvalThreshold = useApprovalThreshold();
  const [contribExceeds, setContribExceeds] = useState(false);  const allRemarkCategories = useRemarkCategories();
  const [loading, setLoading] = useState(false);
  const [showNewTransporter, setShowNewTransporter] = useState(false);
  const [transporterToDelete, setTransporterToDelete] = useState(null);
  const [newTransporterName, setNewTransporterName] = useState("");
  const [newTransporterId, setNewTransporterId] = useState("");

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [sellersRes, productsRes, slabsRes, quotationIdRes, transportersRes] = await Promise.all([
        axios.get(`${API}/sellers`, { headers: { Authorization: `Bearer ${getAuthToken()}` } }),
        axios.get(`${API}/products`, { headers: { Authorization: `Bearer ${getAuthToken()}` } }),
        axios.get(`${API}/discount-slabs`, { headers: { Authorization: `Bearer ${getAuthToken()}` } }),
        axios.get(`${API}/quotations/next-id`, { headers: { Authorization: `Bearer ${getAuthToken()}` } }),
        axios.get(`${API}/transporters`, { headers: { Authorization: `Bearer ${getAuthToken()}` } }),
      ]);
      setSellers(sellersRes.data);
      setProducts(productsRes.data);
      setDiscountSlabs(slabsRes.data);
      setNextQuotationId(quotationIdRes.data.next_quotation_id);
      setTransporters(transportersRes.data);
    } catch (error) {
      toast.error("Error fetching data");
    }
  };

  const handleAddTransporter = async () => {
    if (!newTransporterName.trim()) {
      toast.error("Transporter name is required");
      return;
    }
    try {
      const response = await axios.post(`${API}/transporters`, {
        transporter_name: newTransporterName,
        transporter_id: newTransporterId
      }, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      setTransporters([...transporters, response.data]);
      setSelectedTransporter(response.data);
      setShowNewTransporter(false);
      setNewTransporterName("");
      setNewTransporterId("");
      toast.success("Transporter added successfully");
    } catch (error) {
      toast.error("Error adding transporter");
    }
  };

  const handleDeleteTransporter = async (transporterId) => {
    try {
      await axios.delete(`${API}/transporters/${transporterId}`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      setTransporters(transporters.filter(t => t.id !== transporterId));
      if (selectedTransporter?.id === transporterId) {
        setSelectedTransporter(null);
      }
      toast.success("Transporter deleted successfully");
    } catch (error) {
      toast.error("Error deleting transporter");
    }
  };

  const addSaleItem = () => {
    setSaleItems([...saleItems, {
      product_id: "",
      product_name: "",
      sku_id: "",
      item_code: "",
      brand: "",
      mrp: 0,
      quantity: 1,
      discount: 0,
      item_type: "sale",
      exclude_from_discount: false,
      total_mrp: 0,
      invoice_value: 0
    }]);
  };

  const addFreebieItem = () => {
    setFreebieItems([...freebieItems, {
      product_id: "",
      product_name: "",
      sku_id: "",
      item_code: "",
      brand: "",
      mrp: 0,
      quantity: 1,
      discount: 100,
      item_type: "freebie",
      exclude_from_discount: false,
      total_mrp: 0,
      invoice_value: 0
    }]);
  };

  const removeSaleItem = (index) => {
    setSaleItems(saleItems.filter((_, i) => i !== index));
  };

  const removeFreebieItem = (index) => {
    setFreebieItems(freebieItems.filter((_, i) => i !== index));
  };

  const updateSaleItem = (index, field, value) => {
    const updated = [...saleItems];
    const item = updated[index];
    
    if (field === "product_id") {
      const product = products.find(p => p.id === value);
      if (product) {
        item.product_id = product.id;
        item.product_name = product.product_name;
        item.sku_id = product.sku_id;
        item.item_code = product.item_code || "";
        item.brand = product.brand;
        item.mrp = product.mrp;
        item.exclude_from_discount = product.exclude_from_discount || false;
      }
    } else if (field === "quantity") {
      item.quantity = value === "" ? 0 : (parseInt(value) || 0);
    } else if (field === "discount") {
      item.discount = value === "" ? 0 : (parseFloat(value) || 0);
    }
    
    item.total_mrp = item.mrp * item.quantity;
    item.invoice_value = item.total_mrp * (1 - item.discount / 100);
    
    setSaleItems(updated);
  };

  const updateFreebieItem = (index, field, value) => {
    const updated = [...freebieItems];
    const item = updated[index];
    
    if (field === "product_id") {
      const product = products.find(p => p.id === value);
      if (product) {
        item.product_id = product.id;
        item.product_name = product.product_name;
        item.sku_id = product.sku_id;
        item.item_code = product.item_code || "";
        item.brand = product.brand;
        item.mrp = product.mrp;
        item.exclude_from_discount = product.exclude_from_discount || false;
      }
    } else if (field === "quantity") {
      item.quantity = value === "" ? 0 : (parseInt(value) || 0);
    }
    
    item.total_mrp = item.mrp * item.quantity;
    item.invoice_value = 0;
    item.discount = 100;
    
    setFreebieItems(updated);
  };

  // Memoized once per products load — avoids rebuilding option objects for every item row on
  // every keystroke (major edit/create-screen lag source with large product catalogs).
  const productOptions = useMemo(
    () => (products || []).map((p) => ({
      value: p.id,
      label: p.product_name,
      searchTerms: [p.product_name, p.sku_id, p.item_code, p.brand].filter(Boolean),
    })),
    [products]
  );

  // NEW DISCOUNT ENGINE - Calculate totals with SKU-level exclusion logic
  const calculateTotals = () => {
    // Total values (ALL items)
    const totalMrpValue = saleItems.reduce((sum, item) => sum + item.total_mrp, 0);
    const totalInvoiceValue = saleItems.reduce((sum, item) => sum + item.invoice_value, 0);
    const totalDiscountValue = totalMrpValue - totalInvoiceValue;
    const freebiesValue = freebieItems.reduce((sum, item) => sum + item.total_mrp, 0);
    
    // ELIGIBLE values (only items where exclude_from_discount = false)
    const eligibleSaleItems = saleItems.filter(item => !item.exclude_from_discount);
    const eligibleFreebieItems = freebies.filter(item => !item.exclude_from_discount);
    
    const eligibleMrp = eligibleSaleItems.reduce((sum, item) => sum + item.total_mrp, 0) +
                        eligibleFreebieItems.reduce((sum, item) => sum + item.total_mrp, 0);
    const eligibleInvoiceValue = eligibleSaleItems.reduce((sum, item) => sum + item.invoice_value, 0);
    
    // EFFECTIVE DISCOUNT % = ((Eligible MRP - Invoice Value) / Eligible MRP) × 100
    const effectiveDiscount = eligibleMrp > 0 
      ? ((eligibleMrp - eligibleInvoiceValue) / eligibleMrp) * 100 
      : 0;
    
    // BRAND-WISE CALCULATIONS (matches overall logic: includes eligible freebies, excludes shakers/carry bags)
    const brandStats = {};
    const brands = ["Wellcore", "Dynamite", "Okami", "YouWeFit", "Zero Sugar", "OSMO"];
    
    brands.forEach(brand => {
      // Step 1: Get eligible sale items for this brand (exclude_from_discount = false)
      const brandEligibleSaleItems = saleItems.filter(item => 
        item.brand === brand && !item.exclude_from_discount
      );
      
      // Step 2: Get eligible freebie items for this brand (exclude_from_discount = false)
      const brandEligibleFreebieItems = freebieItems.filter(item => 
        item.brand === brand && !item.exclude_from_discount
      );
      
      // Step 3: Calculate Total Brand MRP Value (eligible sale MRP + eligible freebie MRP)
      const brandSaleMrp = brandEligibleSaleItems.reduce((sum, item) => sum + item.total_mrp, 0);
      const brandFreebieMrp = brandEligibleFreebieItems.reduce((sum, item) => sum + item.total_mrp, 0);
      const brandTotalMrp = brandSaleMrp + brandFreebieMrp;
      
      // Step 4: Calculate Brand Invoice Value (only from eligible sale items)
      const brandInvoice = brandEligibleSaleItems.reduce((sum, item) => sum + item.invoice_value, 0);
      
      // Step 5: Apply formula - Brand Effective Discount % = (Total Brand MRP - Brand Invoice) / Total Brand MRP × 100
      const brandDiscount = brandTotalMrp > 0 
        ? ((brandTotalMrp - brandInvoice) / brandTotalMrp) * 100 
        : 0;
      
      // Only include brand if there's any eligible value
      if (brandTotalMrp > 0) {
        brandStats[brand] = {
          mrp: brandTotalMrp,
          saleMrp: brandSaleMrp,
          freebieMrp: brandFreebieMrp,
          invoice: brandInvoice,
          discount: brandDiscount
        };
      }
    });
    
    // FREEBIE % CALCULATIONS (per item and total)
    const freebiePercentages = freebieItems.map(item => ({
      ...item,
      freebiePercent: totalInvoiceValue > 0 ? (item.total_mrp / totalInvoiceValue) * 100 : 0
    }));
    
    const totalFreebiePercent = totalInvoiceValue > 0 ? (freebiesValue / totalInvoiceValue) * 100 : 0;
    
    // ALLOWED DISCOUNT % - NEW LOGIC
    let allowedDiscount = 0;
    
    if (eligibleInvoiceValue > 0) {
      // Step 2: Determine Base Discount from slab
      let baseDiscount = 0;
      if (eligibleInvoiceValue <= 300000) baseDiscount = 61;
      else if (eligibleInvoiceValue <= 500000) baseDiscount = 62;
      else if (eligibleInvoiceValue <= 1500000) baseDiscount = 62.5;
      else if (eligibleInvoiceValue <= 5000000) baseDiscount = 63;
      else if (eligibleInvoiceValue <= 10000000) baseDiscount = 64.2;
      else baseDiscount = 65.2;
      
      // Step 3: Calculate brand-wise invoice contribution (only eligible items)
      const brandInvoice = {
        "Wellcore": 0,
        "Dynamite": 0,
        "Okami": 0,
        "YouWeFit": 0,
        "Zero Sugar": 0,
        "OSMO": 0
      };
      
      eligibleSaleItems.forEach(item => {
        if (item.brand in brandInvoice) {
          brandInvoice[item.brand] += item.invoice_value;
        }
      });
      
      // Step 4: Additional Discount (+1% per qualifying brand)
      // Applicable brands: Dynamite, YouWeFit, Okami, ZeroSugar
      // Condition: Brand Invoice Value >= 25% of Total Invoice Value
      let additionalDiscount = 0;
      const qualifyingBrands = ["Dynamite", "YouWeFit", "Okami", "Zero Sugar"];
      const threshold = eligibleInvoiceValue * 0.25;
      
      qualifyingBrands.forEach(brand => {
        if (brandInvoice[brand] >= threshold) {
          additionalDiscount += 1;
        }
      });
      
      // Step 5: Total Allowed Discount
      allowedDiscount = baseDiscount + additionalDiscount;
      
      // Step 6: Apply STRICT CAP of 63%
      allowedDiscount = Math.min(allowedDiscount, 63);
    }
    
    const finalStatus = effectiveDiscount <= allowedDiscount ? "valid" : "over_discount";
    
    return {
      totalMrpValue,
      totalInvoiceValue,
      totalDiscountValue,
      freebiesValue,
      eligibleMrp,
      eligibleInvoiceValue,
      effectiveDiscount,
      allowedDiscount,
      finalStatus,
      brandStats,
      freebiePercentages,
      totalFreebiePercent
    };
  };

  // Helper to get freebieItems (alias for calculations)
  const freebies = freebieItems;

  const totals = calculateTotals();

  // ─── Real-time warehouse-aware inventory validation (Step 2) ───
  // Fetches Available Qty for the selected Pickup Warehouse (FBW: per-warehouse Available;
  // Gurugram: weekly Remaining Qty for the week of orderDate). Recomputes violations on every
  // qty / sku / warehouse change. Drafts CAN bypass qty violations; finalization is blocked.
  const { available: availableMap, hasInventory: hasWarehouseInventory, warehouseType } =
    useWarehouseAvailability(pickupWarehouseId, orderDate, null);

  // A warehouse deactivated in Warehouse Master while this form is open must not stay selected.
  useEffect(() => {
    if (warehousesLoaded && pickupWarehouseId && !isWarehouseActive(pickupWarehouseId)) {
      setPickupWarehouseId("");
      toast.warning("The selected Pickup Warehouse was deactivated. Please select an active warehouse.");
    }
  }, [warehousesLoaded, pickupWarehouseId, isWarehouseActive]);
  const { violationsBySku: inventoryViolations, hasViolations: hasInventoryViolation } =
    computeInventoryViolations(saleItems, freebieItems, availableMap, hasWarehouseInventory);

  // ─── Slab Intelligence-driven brand-wise discount validation (real-time) ───
  // Replaces the fixed 60% threshold rule. Allowed discount + approval need are determined
  // brand-wise from the Slab Intelligence quarter applicable to the Quotation Date.
  const slabInfo = useApplicableSlab(orderDate);
  const slabVal = computeSlabValidation({
    brandStats: totals.brandStats,
    totalInvoiceValue: totals.totalInvoiceValue,
    slabs: slabInfo.slabs,
  });
  const slabRequiresApproval = slabVal.requiresApproval;
  const slabDiscountOver = slabRequiresApproval;


  const handleSubmit = async (saveAsDraft = false) => {
    if (!selectedSeller) {
      toast.error("Please select a seller");
      return;
    }

    // Pickup Warehouse is mandatory for every quotation (draft & final).
    if (!pickupWarehouseId || !isWarehouseActive(pickupWarehouseId)) {
      toast.error("Please select an active Pickup Warehouse");
      return;
    }

    // When Slab Intelligence flags this quotation for approval, Remarks + at least one Remark
    // Category are mandatory (draft & final) so it can be submitted for Manager Approval.
    if (slabDiscountOver) {
      if (!approvalRemarks.trim()) {
        toast.error("Please add Remarks explaining why this quotation needs manager approval.");
        return;
      }
      if (remarkCategories.length === 0) {
        toast.error("Please select at least one Remark Category.");
        return;
      }
    }

    if (!saveAsDraft) {
      if (saleItems.length === 0) {
        toast.error("Please add at least one sale item");
        return;
      }
      
      if (!selectedTransporter) {
        toast.error("Transporter is mandatory for final quotations");
        return;
      }

      if (slabRequiresApproval) {
        toast.error("This quotation requires Manager Approval. Please Save as Draft and submit for approval.");
        return;
      }

      if (hasInventoryViolation) {
        toast.error("Inventory not available for one or more SKUs. Save as Draft or adjust quantities.");
        return;
      }
    }
    
    setLoading(true);
    try {
      const payload = {
        seller_id: selectedSeller,
        items: saleItems,
        freebies: freebieItems,
        order_date: orderDate, // Include editable order date
        total_mrp_value: totals.totalMrpValue,
        total_invoice_value: totals.totalInvoiceValue,
        total_discount_value: totals.totalDiscountValue,
        freebies_value: totals.freebiesValue,
        effective_discount: totals.effectiveDiscount,
        allowed_discount: totals.allowedDiscount,
        approval_override: approvalOverride,
        approval_remarks: approvalRemarks,
        remarks: approvalRemarks, // Single source of truth — frontend now exposes only Approval Remarks.
        remark_categories: remarkCategories,
        final_status: totals.finalStatus,
        transporter_id: selectedTransporter?.id || null,
        transporter_name: selectedTransporter?.transporter_name || null,
        transporter_code: selectedTransporter?.transporter_id || null,
        quotation_type: quotationType || "offline",
        pickup_warehouse_id: pickupWarehouseId,
        is_draft: saveAsDraft
      };
      
      await axios.post(`${API}/quotations`, payload, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      
      if (saveAsDraft) {
        toast.success("Draft saved successfully");
        navigate("/drafts");
      } else {
        toast.success("Quotation created successfully");
        navigate("/quotations-log");
      }
    } catch (error) {
      toast.error(extractErrorMessage(error, "Error creating quotation"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-[#0F0F0F]" data-testid="create-quotation-title">Create Quotation</h1>
        <p className="text-muted-foreground mt-1">Enter quotation details with real-time discount validation</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Quotation Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="order-date">Order Date *</Label>
              <Input 
                id="order-date"
                type="date" 
                value={orderDate} 
                onChange={(e) => setOrderDate(e.target.value)}
                required
                data-testid="quotation-date" 
              />
            </div>
            <div className="space-y-2">
              <Label>Quotation ID</Label>
              <Input value={nextQuotationId} disabled className="font-mono" data-testid="quotation-id" />
            </div>
            <div className="space-y-2">
              <Label>Seller *</Label>
              <SearchableSelect
                options={sellers.map(s => {
                  const addrParts = [s.shipping_address, s.city, s.state, s.pincode].filter(Boolean);
                  const fullAddr = addrParts.join(", ");
                  return {
                    value: s.id,
                    label: `${s.seller_name} (${s.seller_code})`,
                    subtitle: fullAddr.length > 60 ? fullAddr.substring(0, 60) + "..." : fullAddr,
                    tooltip: `${s.seller_name} (${s.seller_code}) – ${fullAddr}`,
                    searchTerms: [s.seller_name, s.seller_code, s.city, s.state, s.shipping_address, s.pincode]
                  };
                })}
                value={selectedSeller}
                onValueChange={setSelectedSeller}
                placeholder="Search seller..."
                searchPlaceholder="Type to search..."
                data-testid="seller-select"
              />
            </div>
          </div>
          
          {/* Quotation Type + Pickup Warehouse — placed after Seller, before Transporter */}
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="quotation-type">Quotation Type *</Label>
              <Select value={quotationType} onValueChange={setQuotationType}>
                <SelectTrigger id="quotation-type" data-testid="quotation-type-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="offline" data-testid="quotation-type-option-offline">Offline</SelectItem>
                  <SelectItem value="online" data-testid="quotation-type-option-online">Online</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Pickup Warehouse *</Label>
              <SearchableSelect
                options={warehouseOptions}
                onOpenChange={(o) => o && refreshWarehouses()}
                value={pickupWarehouseId}
                onValueChange={setPickupWarehouseId}
                placeholder="Select warehouse..."
                searchPlaceholder="Type to search..."
                data-testid="pickup-warehouse-select"
              />
              {(() => { const w = activeWarehouses.find((x) => x.id === pickupWarehouseId); return w ? (
                <p className="text-xs text-muted-foreground" data-testid="pickup-warehouse-address">{w.address || "No pickup address configured in Warehouse Master"}</p>
              ) : null; })()}
              {pickupWarehouseId && warehouseType === "fbw_inventory" && !hasWarehouseInventory && (
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-300 rounded-md px-2 py-1.5" data-testid="pickup-warehouse-no-stock-note">
                  No live stock recorded for this MCF warehouse yet — stock validation starts once GRN is received on a stock transfer.
                </p>
              )}
            </div>
          </div>

          {/* Transporter Section */}
          <div className="space-y-2">
            <Label>Transporter *</Label>
            <div className="flex gap-2">
              <div className="flex-1">
                <SearchableSelect
                  options={[
                    { value: "__new__", label: "+ Add New Transporter" },
                    ...transporters.map(t => ({
                      value: t.id,
                      label: `${t.transporter_name}${t.transporter_id ? ` (${t.transporter_id})` : ''}`,
                      searchTerms: [t.transporter_name, t.transporter_id]
                    }))
                  ]}
                  value={selectedTransporter?.id || ""}
                  onValueChange={(val) => {
                    if (val === "__new__") {
                      setShowNewTransporter(true);
                      setSelectedTransporter(null);
                    } else {
                      const transporter = transporters.find(t => t.id === val);
                      setSelectedTransporter(transporter || null);
                      setShowNewTransporter(false);
                    }
                  }}
                  placeholder="Select transporter..."
                  searchPlaceholder="Type to search..."
                  data-testid="transporter-select"
                />
              </div>
              {selectedTransporter && (
                <>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedTransporter(null)}>
                    Clear
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Transporter?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to delete "{selectedTransporter.transporter_name}"? This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => handleDeleteTransporter(selectedTransporter.id)}
                          className="bg-destructive hover:bg-destructive/90"
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </>
              )}
            </div>
          </div>
          
          {/* Add New Transporter Form */}
          {showNewTransporter && (
            <div className="p-4 border rounded-lg bg-slate-50 space-y-3">
              <h4 className="font-medium">Add New Transporter</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="transporter-name">Transporter Name *</Label>
                  <Input
                    id="transporter-name"
                    value={newTransporterName}
                    onChange={(e) => setNewTransporterName(e.target.value)}
                    placeholder="Enter transporter name"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="transporter-id">Transporter ID (Optional)</Label>
                  <Input
                    id="transporter-id"
                    value={newTransporterId}
                    onChange={(e) => setNewTransporterId(e.target.value)}
                    placeholder="Enter transporter ID"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleAddTransporter} className="bg-accent hover:bg-accent/90 text-white">
                  Add Transporter
                </Button>
                <Button size="sm" variant="outline" onClick={() => setShowNewTransporter(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Sale Items</CardTitle>
          <Button onClick={addSaleItem} size="sm" className="bg-accent hover:bg-accent/90 text-white" data-testid="add-sale-item-button">
            <Plus className="h-4 w-4 mr-2" />
            Add Item
          </Button>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[200px]">Product</TableHead>
                  <TableHead>SKU ID</TableHead>
                  <TableHead>Item Code</TableHead>
                  <TableHead>Brand</TableHead>
                  <TableHead>MRP</TableHead>
                  <TableHead>Qty</TableHead>
                  <TableHead>Available</TableHead>
                  <TableHead>Disc %</TableHead>
                  <TableHead>Total MRP</TableHead>
                  <TableHead>Invoice Val</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {saleItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center py-8 text-muted-foreground">
                      No items added. Click "Add Item" to start.
                    </TableCell>
                  </TableRow>
                ) : (
                  saleItems.map((item, index) => (
                    <TableRow key={`sale-${item.product_id || index}`} data-testid={`sale-item-${index}`}>
                      <TableCell>
                        <SearchableSelect
                          options={productOptions}
                          value={item.product_id}
                          onValueChange={(value) => updateSaleItem(index, "product_id", value)}
                          placeholder="Search product..."
                          searchPlaceholder="Search by name, SKU..."
                          data-testid={`sale-product-select-${index}`}
                        />
                      </TableCell>
                      <TableCell className="font-mono text-sm">{item.sku_id}</TableCell>
                      <TableCell className="font-mono text-sm text-muted-foreground">{item.item_code || "-"}</TableCell>
                      <TableCell>{(() => { const p = getBrandPillStyle(item.brand); return <span className="px-2 py-1 rounded-full text-xs font-medium" style={{ background: p.bg, color: p.text }}>{item.brand}</span>; })()}</TableCell>
                      <TableCell className="font-mono">₹{item.mrp}</TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          value={item.quantity || ""}
                          onChange={(e) => updateSaleItem(index, "quantity", e.target.value)}
                          className={`w-20 ${inventoryViolations[item.sku_id] ? "border-destructive ring-1 ring-destructive/40 focus-visible:ring-destructive" : ""}`}
                          placeholder="Qty"
                          data-testid={`sale-quantity-${index}`}
                        />
                        {inventoryViolations[item.sku_id] && (
                          <p
                            className="text-xs text-destructive mt-1 leading-tight whitespace-nowrap"
                            data-testid={`sale-inventory-error-${index}`}
                          >
                            Insufficient inventory. Available: {inventoryViolations[item.sku_id].remaining} units
                          </p>
                        )}
                      </TableCell>
                      <TableCell data-testid={`sale-available-${index}`}>
                        {item.sku_id && hasWarehouseInventory ? (
                          <span className={`font-mono text-sm ${inventoryViolations[item.sku_id] ? "text-destructive font-semibold" : "text-green-600"}`}>
                            {availableMap[item.sku_id] ?? 0}
                          </span>
                        ) : (
                          <span className="text-muted-foreground text-sm">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          step="0.01"
                          value={item.discount || ""}
                          onChange={(e) => updateSaleItem(index, "discount", e.target.value)}
                          className="w-20"
                          placeholder="Disc %"
                          data-testid={`sale-discount-${index}`}
                        />
                      </TableCell>
                      <TableCell className="font-mono">₹{item.total_mrp.toFixed(2)}</TableCell>
                      <TableCell className="font-mono font-semibold">₹{item.invoice_value.toFixed(2)}</TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive"
                          onClick={() => removeSaleItem(index)}
                          data-testid={`remove-sale-item-${index}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Freebies (100% Discount)</CardTitle>
            {totals.totalFreebiePercent > 0 && (
              <p className="text-sm text-muted-foreground mt-1">
                Total Freebie %: <span className="font-mono font-semibold text-amber-600">{totals.totalFreebiePercent.toFixed(2)}%</span> of Invoice Value
              </p>
            )}
          </div>
          <Button onClick={addFreebieItem} size="sm" variant="outline" data-testid="add-freebie-button">
            <Plus className="h-4 w-4 mr-2" />
            Add Freebie
          </Button>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[200px]">Product</TableHead>
                  <TableHead>SKU ID</TableHead>
                  <TableHead>Item Code</TableHead>
                  <TableHead>Brand</TableHead>
                  <TableHead>MRP</TableHead>
                  <TableHead>Qty</TableHead>
                  <TableHead>Available</TableHead>
                  <TableHead>Total MRP</TableHead>
                  <TableHead>Freebie %</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {freebieItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                      No freebies added.
                    </TableCell>
                  </TableRow>
                ) : (
                  freebieItems.map((item, index) => {
                    const freebiePercent = totals.totalInvoiceValue > 0 
                      ? (item.total_mrp / totals.totalInvoiceValue) * 100 
                      : 0;
                    return (
                      <TableRow key={`freebie-${item.product_id || index}`} data-testid={`freebie-item-${index}`}>
                        <TableCell>
                          <SearchableSelect
                            options={productOptions}
                            value={item.product_id}
                            onValueChange={(value) => updateFreebieItem(index, "product_id", value)}
                            placeholder="Search product..."
                            searchPlaceholder="Search by name, SKU..."
                            data-testid={`freebie-product-select-${index}`}
                          />
                        </TableCell>
                        <TableCell className="font-mono text-sm">{item.sku_id}</TableCell>
                        <TableCell className="font-mono text-sm text-muted-foreground">{item.item_code || "-"}</TableCell>
                        <TableCell>{(() => { const p = getBrandPillStyle(item.brand); return <span className="px-2 py-1 rounded-full text-xs font-medium" style={{ background: p.bg, color: p.text }}>{item.brand}</span>; })()}</TableCell>
                        <TableCell className="font-mono">₹{item.mrp}</TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            value={item.quantity || ""}
                            onChange={(e) => updateFreebieItem(index, "quantity", e.target.value)}
                            className={`w-20 ${inventoryViolations[item.sku_id] ? "border-destructive ring-1 ring-destructive/40 focus-visible:ring-destructive" : ""}`}
                            placeholder="Qty"
                            data-testid={`freebie-quantity-${index}`}
                          />
                          {inventoryViolations[item.sku_id] && (
                            <p
                              className="text-xs text-destructive mt-1 leading-tight whitespace-nowrap"
                              data-testid={`freebie-inventory-error-${index}`}
                            >
                              Insufficient inventory. Available: {inventoryViolations[item.sku_id].remaining} units
                            </p>
                          )}
                        </TableCell>
                        <TableCell data-testid={`freebie-available-${index}`}>
                          {item.sku_id && hasWarehouseInventory ? (
                            <span className={`font-mono text-sm ${inventoryViolations[item.sku_id] ? "text-destructive font-semibold" : "text-green-600"}`}>
                              {availableMap[item.sku_id] ?? 0}
                            </span>
                          ) : (
                            <span className="text-muted-foreground text-sm">—</span>
                          )}
                        </TableCell>
                        <TableCell className="font-mono">₹{item.total_mrp.toFixed(2)}</TableCell>
                        <TableCell className="font-mono font-semibold text-amber-600">
                          {freebiePercent.toFixed(2)}%
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-destructive"
                            onClick={() => removeFreebieItem(index)}
                            data-testid={`remove-freebie-${index}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Summary & Validation</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Total MRP</p>
              <p className="text-2xl font-bold font-mono" data-testid="total-mrp">₹{totals.totalMrpValue.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Invoice Value</p>
              <p className="text-2xl font-bold font-mono" data-testid="invoice-value">₹{totals.totalInvoiceValue.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Discount Value</p>
              <p className="text-2xl font-bold font-mono" data-testid="discount-value">₹{totals.totalDiscountValue.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Freebies Value</p>
              <p className="text-2xl font-bold font-mono" data-testid="freebies-value">₹{totals.freebiesValue.toFixed(2)}</p>
            </div>
          </div>

          {/* Eligible MRP info for discount calculation */}
          {totals.eligibleMrp !== totals.totalMrpValue + totals.freebiesValue && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
              <p className="font-medium text-amber-800">Some SKUs excluded from discount calculation:</p>
              <p className="text-amber-700">Eligible MRP for discount: ₹{totals.eligibleMrp.toFixed(2)} | Eligible Invoice: ₹{totals.eligibleInvoiceValue.toFixed(2)}</p>
            </div>
          )}

          <div className={`p-4 rounded-lg border-2 ${
            (!slabRequiresApproval && !contribExceeds)
              ? "bg-success/10 border-success"
              : "bg-destructive/10 border-destructive"
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {(!slabRequiresApproval && !contribExceeds) ? (
                  <CheckCircle2 className="h-5 w-5 text-success" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-destructive" />
                )}
                <div>
                  <p className="font-semibold">Overall Effective Discount: <span className="font-mono" data-testid="effective-discount">{totals.effectiveDiscount.toFixed(2)}%</span></p>
                  <p className="text-xs text-muted-foreground">
                    Informational only · Approval is determined brand-wise via Slab Intelligence
                    {slabInfo.quarterLabel ? ` (${slabInfo.quarterLabel})` : ""}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className={`text-lg font-bold ${
                  (!slabRequiresApproval && !contribExceeds) ? "text-success" : "text-destructive"
                }`} data-testid="quotation-status">
                  {(slabRequiresApproval || contribExceeds) ? "Manager Approval Required" : "Within Limit"}
                </p>
              </div>
            </div>
          </div>

          {/* Brand-wise Effective Discount + Slab Eligibility (Slab Intelligence-driven) */}
          {slabVal.brands.length > 0 && (
            <div className="p-4 bg-slate-50 rounded-lg border" data-testid="create-brand-wise-discount">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-1">
                <p className="font-semibold text-sm">Brand-wise Effective Discount &amp; Slab Eligibility</p>
                {slabInfo.quarterLabel && (
                  <span className="text-xs text-muted-foreground">
                    Slab: {slabInfo.quarterLabel}
                    {slabVal.applicableSlab?.range_label ? ` · ${slabVal.applicableSlab.range_label}` : " · No matching slab"}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {slabVal.brands.map((b) => {
                  const bad = b.status === "approval";
                  return (
                    <div
                      key={b.brand}
                      className={`p-3 rounded border-2 ${bad ? "bg-red-50 border-red-400" : "bg-green-50 border-green-400"}`}
                      data-testid={`create-brand-stat-${b.brand}`}
                    >
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-semibold text-[#0F0F0F]">{b.brand}</p>
                        <span
                          className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${bad ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}`}
                          data-testid={`create-brand-status-${b.brand}`}
                        >
                          {bad ? "Approval Required" : "Within Limit"}
                        </span>
                      </div>
                      <div className="mt-1.5 space-y-0.5 text-xs">
                        <p className="flex justify-between"><span className="text-muted-foreground">Contribution</span><span className="font-mono font-medium">{b.contribution.toFixed(2)}%</span></p>
                        {b.hasContributionRule && (
                          <p className="flex justify-between"><span className="text-muted-foreground">Required Contribution</span><span className="font-mono">{b.requiredRangeLabel || "—"}</span></p>
                        )}
                        <p className="flex justify-between"><span className="text-muted-foreground">Effective Discount</span><span className="font-mono font-medium">{b.effectiveDiscount.toFixed(2)}%</span></p>
                        <p className="flex justify-between"><span className="text-muted-foreground">Allowed Discount</span><span className={`font-mono font-medium ${bad ? "text-red-600" : "text-green-700"}`}>{b.allowedLabel}</span></p>
                      </div>
                      {bad && (
                        <p className="mt-1.5 text-[11px] text-red-600 leading-tight" data-testid={`create-brand-reason-${b.brand}`}>{b.reason}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Seller Business Contribution (Phase 1 calc + Phase 2 validation indicator) */}
          <SellerContributionCard
            sellerId={selectedSeller}
            month={orderDate ? orderDate.slice(0, 7) : ""}
            currentQuotationValue={totals.totalInvoiceValue}
            onValidationChange={(v) => setContribExceeds(!!v.exceeds)}
          />

          {/* Remarks block — visible for every quotation. Mandatory when approval is required. */}
          <div className="space-y-4 p-4 bg-slate-100 rounded-lg">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="approval_remarks" className="text-sm font-medium">
                  Remarks
                  {slabDiscountOver ? (
                    <span className="text-red-600"> *</span>
                  ) : (
                    <span className="ml-2 text-[10px] font-normal text-muted-foreground">(optional)</span>
                  )}
                </Label>
                <Textarea
                  id="approval_remarks"
                  value={approvalRemarks}
                  onChange={(e) => setApprovalRemarks(e.target.value)}
                  placeholder={slabDiscountOver
                    ? "Enter the reason this quotation needs manager approval..."
                    : "Add notes about this quotation (optional)…"}
                  className="min-h-[80px]"
                  data-testid="approval-remarks-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  Remark Categories
                  {slabDiscountOver ? (
                    <>
                      <span className="text-red-600"> *</span>
                      <span className="ml-2 text-[10px] font-normal text-muted-foreground">(at least one)</span>
                    </>
                  ) : (
                    <span className="ml-2 text-[10px] font-normal text-muted-foreground">(optional · multi-select)</span>
                  )}
                </Label>
                <MultiSelectChips
                  options={allRemarkCategories}
                  value={remarkCategories}
                  onChange={setRemarkCategories}
                  placeholder="Pick one or more categories"
                  required={slabDiscountOver}
                  testId="quotation-remark-categories"
                />
              </div>
            </div>
          </div>

          {(slabRequiresApproval || contribExceeds) && (
            <div className="p-4 rounded-lg border-2 border-amber-400 bg-amber-50" data-testid="approval-required-banner">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-amber-700 mt-0.5 shrink-0" />
                <div className="text-sm">
                  <p className="font-semibold text-amber-900">Manager Approval Required</p>
                  <ul className="text-amber-800 mt-1 list-disc pl-4 space-y-0.5">
                    {slabVal.reasons.map((r, i) => (
                      <li key={i} data-testid={`approval-reason-${i}`}>{r}</li>
                    ))}
                    {contribExceeds && <li>Seller Revenue Contribution exceeds the 25% limit.</li>}
                  </ul>
                  <p className="text-amber-800 mt-1 font-medium">Please <b>Save as Draft</b>, then submit it for approval from the Drafts page.</p>
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <Button
              onClick={() => handleSubmit(true)}
              disabled={loading}
              variant="outline"
              className="flex-1"
              data-testid="save-draft-button"
            >
              {loading ? "Saving..." : "Save as Draft"}
            </Button>
            <Button
              onClick={() => handleSubmit(false)}
              disabled={loading || !selectedTransporter || hasInventoryViolation || slabRequiresApproval || contribExceeds}
              className="flex-1 bg-accent hover:bg-accent/90 text-white"
              data-testid="submit-quotation-button"
              title={(slabRequiresApproval || contribExceeds) ? "Manager approval required — Save as Draft and submit for approval" : (hasInventoryViolation ? "Resolve inventory shortages before creating the quotation" : undefined)}
            >
              {loading ? "Creating Quotation..." : "Create Quotation"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
